<template>
    <div class="d-flex text-grey-darker w-100">
        <div class="w-1/3">
            <h5 class="mb-3">Choose permissions</h5>
            <ul class="list-unstyled mt-2">
                <li
                    v-for="item in sidebarItems"
                    :data-value="item.id"
                    :class="{ active : item.id === selectedSidebarItemId }"
                    class="font-weight-bold py-2 cursor-pointer"
                    @click="selectedSidebarItemId = item.id">
                    {{ item.label }}
                </li>
            </ul>
        </div>
        <div class="w-2/3">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h4>Permissions</h4>
                <label class="custom-checkbox mb-0">
                    <input type="checkbox" v-model="expandAllCollapsableItems" @change="toggleAllAccordion" />
                    <span class="checkmark"></span>
                    Expand all permissions
                </label>
            </div>

            <div class="permission-section">
                <div v-if="permissionLoader" class="loader-overlay">
                    <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                </div>
                <div id="accordionPermission">
                    <div
                        v-for="(item, index) in collapsableItems"
                        :key="'permission-collapse-'+index"
                        v-if="checkVisible(item)"
                        class="card mb-0">
                        <div class="card-header custom-card-header" :id="item.property + 'heading'">
                            <h5 class="mb-0">
                                <button
                                    data-toggle="collapse"
                                    :data-target="'#collapse' + item.property"
                                    aria-expanded="false"
                                    :aria-controls="'#collapse' + item.property"
                                    class="btn btn-link custom-btn-link text-grey-darker"
                                    @click="toggleAccordion(item.property)">
                                    <span class="float-left"><i class="fa fa-angle-right text-primary mr-3"></i>{{ formatLabel(item.title) }}</span><br>
                                </button>
                            </h5>
                        </div>
                        <div
                            :id="'#collapse' + item.property"
                            class="collapse"
                            :class="{ show: item.expanded }"
                            :aria-labelledby="item.property + 'heading'"
                            data-parent="#accordionPermission">
                            <div class="card-body custom-card-body bg-white">
                                <div class="col-12">
                                    <div
                                        v-for="(scopes, key) in item.content"
                                        :key="'action' + key"
                                        class="d-flex justify-content-between align-items-center mt-2">
                                        <span v-if="!['company-setting', 'enterprise'].includes(item.property)">{{ formatLabel(key) }}</span>
                                        <div v-if="['company-setting', 'enterprise'].includes(item.property)">
                                            <label
                                                v-for="scope in scopes"
                                                class="custom-checkbox mb-3">
                                                <input
                                                    type="checkbox"
                                                    :id="scope"
                                                    :value="scope"
                                                    v-model="selectedPermissions[item.property][key]"/>
                                                <span class="checkmark"></span>
                                                {{ formatLabel(scope) }}
                                            </label>
                                        </div>
                                        <div v-else class="d-flex flex-column align-items-end w-1/2">
                                            <multiselect
                                                v-if="item.property === 'erp'"
                                                :options="getOptionsForMultiselect(scopes)"
                                                label="label"
                                                track-by="value"
                                                v-model="selectedPermissions[item.property][key]"
                                                class="mb-1"
                                                multiple>
                                            </multiselect>
                                            <select
                                                v-else
                                                class="form-control mb-1"
                                                @change="onPermissionChange($event, item.property, key)">
                                                <option
                                                    v-for="scope in scopes"
                                                    v-if="scope !== 'unassigned'"
                                                    :selected="selectedPermissions[item.property][key].includes(scope)"
                                                    :value="scope">
                                                    {{ formatLabel(scope) }}
                                                </option>
                                            </select>
                                            <label
                                                v-if="item.property === 'project' && !['export', 'document'].includes(key) && !selectedPermissions[item.property][key].includes('all') && !selectedPermissions[item.property][key].includes('none')"
                                                class="custom-checkbox mb-0">
                                                <input
                                                    type="checkbox"
                                                    :checked="selectedPermissions[item.property][key].includes('unassigned')"
                                                    @change="onUnassignedPermissionChange($event, item.property, key, 'unassigned')" />
                                                <span class="checkmark"></span>
                                                Unassigned
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Multiselect from "vue-multiselect";

const collapsableItemObject = {
    title: null,
    property: null,
    content: null,
    expanded: false
};
export default {
    name: 'choose-permissions',
    components: {Multiselect},
    props: ['team'],
    data() {
        return {
            permissions: [],
            collapsableItems: [],
            selectedPermissions: {},
            expandAllCollapsableItems: false,
            sidebarItems: [
                {id: 'crm-objects', label: 'CRM Objects'},
                {id: 'reporting', label: 'Reporting'},
                {id: 'erp-integration', label: 'ERP Integration'},
                {id: 'company-setting', label: 'Company Settings'},
            ],
            selectedSidebarItemId: 'crm-objects',
            permissionLoader: false
        }
    },
    watch: {
        selectedPermissions: {
            handler: function(newValue) {
                this.$emit('permission-selection-changed', newValue);
            },
            deep: true
        }
    },
    mounted() {
        this.getAllPermissions();
    },
    methods: {
        async getAllPermissions() {
            let self = this;
            self.permissionLoader = true;

            await axios.get('/settings/'+this.team.id+'/permissions/all').then(({data}) => {
                self.permissions = data;
                self.permissionLoader = false;
                self.manageCollapsableItems();
                self.initializeSelections();
            }).catch((error) => {
                self.permissionLoader = false;
            });
        },
        manageCollapsableItems() {
            let self = this;
            self.collapsableItems = [];

            _.forEach(self.permissions, function(value, key) {
                let object = _.cloneDeep(collapsableItemObject);
                object.title = key;
                object.property = key;
                object.content = value;

                self.collapsableItems.push(object);
            });
        },
        initializeSelections() {
            this.collapsableItems.forEach(item => {
                this.$set(this.selectedPermissions, item.property, {});
                Object.keys(item.content).forEach(action => {
                    let value = [];
                    if(_.includes(['project', 'company', 'contact', 'project-management'], item.property)) {
                        value = [action === 'view' ? 'all' : 'none'];
                    }
                    if(_.includes(['report'], item.property)) {
                        value = ['none'];
                    }
                    this.$set(this.selectedPermissions[item.property], action, value);
                });
            });
        },
        toggleAccordion(property) {
            this.collapsableItems.forEach((item) => {
                const element = document.getElementById('collapse' + property);

                if(item.property === property) {
                    item.expanded = !item.expanded;
                    $(element).collapse(item.expanded ? 'show' : 'hide');
                }
                else {
                    item.expanded = false;
                    $(element).collapse('hide');
                }
            });
        },
        toggleAllAccordion() {
            this.collapsableItems.forEach((item, index) => {
                item.expanded = this.expandAllCollapsableItems;
                const element = document.getElementById('collapse' + item.property);
                if(element) {
                    $(element).collapse(this.expandAllCollapsableItems ? 'show' : 'hide');
                }
            });
        },
        formatLabel(label) {
            if(label === 'assigned-those-reports') {
                return 'Assigned to those who report to them';
            }
            if(label === 'erp') {
                return 'ERP';
            }
            if(label === 'erp-integration-tab') {
                return 'ERP Integration';
            }
            if(label === 'company-setting') {
                return 'Company Settings';
            }
            if(label === 'view-edit') {
                return 'View & Edit';
            }
            if(label === 'view-edit-delete') {
                return 'View, Edit & Delete';
            }
            if(label.endsWith('-tab')) {
                label = label.slice(0, -4);
            }

            return label.split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        },
        checkVisible(item) {
            if(['project', 'company', 'calendar', 'contact', 'project-management'].includes(item.property) && this.selectedSidebarItemId === 'crm-objects') {
                return true;
            }
            else if(['report'].includes(item.property) && this.selectedSidebarItemId === 'reporting') {
                return true;
            }
            else if(['erp'].includes(item.property) && this.selectedSidebarItemId === 'erp-integration') {
                return true;
            }
            else if(['company-setting', 'enterprise'].includes(item.property) && this.selectedSidebarItemId === 'company-setting') {
                return true;
            }
            return false;
        },
        onPermissionChange(event, property, key) {
            const newValue = event.target.value;
            if(['project', 'company', 'contact', 'project-management'].includes(property)) {
                if(key === 'delete' && newValue !== 'none') {
                    ['view', 'edit'].forEach(item => {
                        this.$set(this.selectedPermissions[property], item, [newValue]);
                    });
                }
            }
            this.$set(this.selectedPermissions[property], key, [newValue]);
        },
        onUnassignedPermissionChange(event, property, key, newValue) {
            const existing = this.selectedPermissions[property][key] || [];
            if(event.target.checked) {
                this.$set(this.selectedPermissions[property], key, [...existing, newValue]);
            }
            else {
                const updated = existing.filter(val => val !== newValue);
                this.$set(this.selectedPermissions[property], key, updated);
            }
        },
        async mapSavedPermissions(savedPermissions) {
            await this.getAllPermissions();
            Object.keys(savedPermissions).forEach(property => {
                if(!this.selectedPermissions[property]) return;

                Object.keys(savedPermissions[property]).forEach(action => {
                    let value = property === 'erp' ? this.getOptionsForMultiselect(savedPermissions[property][action]) : savedPermissions[property][action];
                    this.$set(this.selectedPermissions[property], action, value);
                });
            });
        },
        resetData() {
            this.selectedSidebarItemId = 'crm-objects';
            this.expandAllCollapsableItems = false;
            this.manageCollapsableItems();
            this.initializeSelections();
        },
        getOptionsForMultiselect(options) {
            return options.map(option => ({
                value: option,
                label: this.formatLabel(option)
            }));
        }
    }
}
</script>

<style scoped>
.permission-section {
    background: #ffffff;
    padding: 20px 0px;
    margin-bottom: 20px;
}
.custom-checkbox {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 16px;
    user-select: none;
    gap: 8px;
}
.custom-checkbox input[type="checkbox"] {
    display: none;
}
.custom-checkbox .checkmark {
    width: 21px;
    height: 21px;
    background-color: #fff;
    border: 2px solid #01B1AF;
    border-radius: 4px;
    display: inline-block;
    position: relative;
    transition: background-color 0.3s, border-color 0.3s;
}
.custom-checkbox input[type="checkbox"]:checked + .checkmark {
    background-color: #01B1AF;
    border-color: #01B1AF;
}
.custom-checkbox input[type="checkbox"]:checked + .checkmark::after {
    content: '';
    position: absolute;
    top: 45%;
    left: 50%;
    width: 7px;
    height: 14px;
    border: solid #fff;
    border-width: 0 3px 3px 0;
    transform: translate(-50%, -50%) rotate(45deg);
}
.card .custom-card-header {
    border-color: rgb(203, 214, 226);
    background-color: rgb(245, 248, 250);
    padding: .5rem .75rem;
    border-width: 1px 1px;
    border-bottom: none;
    border-radius: unset;
}
.card .custom-card-body {
    border-width: 1px 1px;
}
.custom-btn-link {
    text-decoration: none;
    box-shadow: none;
    outline: none;
    font-size: 15px;
    font-weight: 600;
}
.custom-btn-link:hover {
    text-decoration: none;
    color: #01B1AF;
}
.loader-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.7);
    width: 100%;
    height: 100%;
}
</style>
